import axios from 'axios'
import { stringify } from 'qs'
import { delay } from 'lodash'

import { recaptchaTokenValut } from './recaptcha'
import { isPaidUser } from './freeVersionBlock'
import { sendMessage } from './functions'
import { setDailyReportInfo, getCurrentTimestamp, clearToken } from './storage'
import { signOut } from './userAuth'
import { getLanguageCode } from './translation'
import { waitFor } from './api'
import { DISABLE_RECAPTCHA, skipAuthPaths } from './consts.mjs'

const axiosInstance = axios.create({
  paramsSerializer: (p) => stringify(p, { arrayFormat: 'comma' }),
})

const rawAxiosInstance = axios.create({
  paramsSerializer: (p) => stringify(p, { arrayFormat: 'comma' }),
})

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api'

let retry = 0
let retryManyRequest = 0

const getTokenAPI = async () => {
  try {
    if (isPaidUser() || DISABLE_RECAPTCHA === 'true') {
      return await rawAxiosInstance.get(`${API_BASE_URL}/get_token`, {
        withCredentials: true,
      })
    }

    // Wait if recaptchaTokenValut instance is not ready
    if (!recaptchaTokenValut.hasInstance()) {
      await waitFor(500)
    }

    await waitFor(retry * 500)

    const token = await recaptchaTokenValut.getToken()
    const appToken = await rawAxiosInstance.get(`${API_BASE_URL}/get_token`, {
      withCredentials: true,
      params: { recaptchaToken: token },
    })
    retry = 1
    return appToken
  } catch (e) {
    if (retry >= 4) {
      sendMessage('ERROR_MODAL')
      return
    }
    retry += 1
    // Retry with a delay
    await new Promise((resolve) => {
      delay(async () => {
        recaptchaTokenValut.reset()
        resolve(await getTokenAPI())
      }, 1000)
    })
  }
}

let getTokenPromise = null

const getToken = (forceRefresh = false) => {
  let token = window.localStorage.getItem('token')
  const tokenTTL = parseInt(window.localStorage.getItem('token_ttl'), 10)
  const currentTimestamp = Math.floor(Date.now() / 1000)
  const refreshToken =
    (!getTokenPromise && !tokenTTL) || tokenTTL < currentTimestamp
  if (forceRefresh || refreshToken) {
    clearToken()
    token = null
    getTokenPromise = null
  }
  if (!token) {
    if (!getTokenPromise && retry < 4) {
      getTokenPromise = getTokenAPI()
    }

    if (!getTokenPromise) {
      return Promise.resolve(null)
    }

    return getTokenPromise
      .then((response) => {
        token = response.data.token
        window.localStorage.setItem('token', token)
        window.localStorage.setItem('token_ttl', response.data.ttl)
        getTokenPromise = null

        setDailyReportInfo(response.data)
        return token
      })
      .catch((error) => {
        const previousTokenError =
          error.response &&
          error.response.status === 403 &&
          error.response.data &&
          error.response.data.error === 'FORBIDDEN' &&
          error.response.data.description.startsWith('The token')
        const invalidRecaptchaError =
          error.response &&
          error.response.status === 401 &&
          error.response.data &&
          error.response.data.error === 'INVALID_TOKEN' &&
          error.response.data.description.startsWith('Invalid recaptcha token.')
        const missingRecaptchaError =
          error.response &&
          error.response.status === 400 &&
          error.response.data &&
          error.response.data.error === 'BAD_REQUEST' &&
          error.response.data.description.startsWith(
            "Missing 'recaptchaToken' parameter",
          )
        const recaptchaError =
          error.response &&
          error.response.status >= 500 &&
          error.response.status < 600

        if (previousTokenError) {
          const [, , tkn] = error.response.data.description.split(' ')
          window.localStorage.setItem('token', tkn)
          return tkn
        }

        if (invalidRecaptchaError || missingRecaptchaError || recaptchaError) {
          return new Promise((resolve) => {
            delay(() => {
              recaptchaTokenValut.reset()
              resolve(getToken(true))
            }, 1000)
          })
        }

        getTokenPromise = null
        if (
          error.response &&
          error.response.status === 401 &&
          !skipAuthPaths.some((path) => window.location.pathname.includes(path))
        ) {
          signOut(`/${getLanguageCode()}/login?next=${window.location.href}`)
        }
      })
  }
  return Promise.resolve(token)
}

const getTokenTTL = () => window.localStorage.getItem('token_ttl')

// REQUEST INTERCEPTORS

// Bearer token injector
axiosInstance.interceptors.request.use((config) => {
  const conf = { ...config }
  conf.headers.ts = getCurrentTimestamp()
  // eslint-disable-next-line no-underscore-dangle
  if (conf._skipAuth) {
    return Promise.resolve(conf)
  }
  return getToken().then((token) => {
    conf.headers.Authorization = `Bearer ${token}`
    return conf
  })
})

// Re-captcha retries
axiosInstance.interceptors.response.use(
  (response) => response,
  async (err) => {
    const error = { ...err }
    const { response } = error || {}

    const {
      status,
      headers: {
        'x-ratelimit-requestcost': weight = 100,
        'x-ratelimit-reset': resetTime = Math.floor(Date.now() / 1000),
        'x-ratelimit-remaining': remaining = 0,
      } = {},
    } = response || {}

    const responseData = isJsonBlob(response?.data)
      ? await response?.data?.text()
      : response?.data || {}
    const data =
      typeof responseData === 'string' ? JSON.parse(responseData) : responseData

    const isBearerTokenExpired =
      status === 429 && data?.error === 'TOO_MANY_REQUEST'
    const isReportLimitBulkAnalysis =
      status === 403 &&
      data?.error === 'REPORT_LIMIT_REACHED' &&
      response.config.url === '/keywords_bulk_analysis'
    const isReportLimitReached =
      status === 403 && data?.error === 'REPORT_LIMIT_REACHED'
    const isKeywordMetricLimitReached =
      status === 403 &&
      data?.error === 'REPORT_LIMIT_REACHED' &&
      data?.code === 'monthly_keyword_metrics_updates'
    const isBearerTokenInvalid =
      status === 401 && data?.error === 'INVALID_TOKEN'
    const isInvalidUserSession =
      status === 401 && data?.error === 'INVALID_USER_SESSION'

    if (isBearerTokenExpired) {
      if (retryManyRequest >= 4) {
        sendMessage('LIMIT_REACHED')
        return Promise.reject(error)
      }
      if (resetTime > Math.floor(Date.now() / 1000)) {
        if (remaining === 0 || remaining < weight) {
          sendMessage('LIMIT_REACHED')
        }
        return Promise.reject(error)
      }
      return getToken(resetTime <= Math.floor(Date.now() / 1000)).then(
        async (token) => {
          error.config.headers.Authorization = `Bearer ${token}`
          retryManyRequest += 1
          await waitFor(retryManyRequest * 1000)
          return axiosInstance(error.config)
        },
      )
    }
    if (isKeywordMetricLimitReached) {
      sendMessage('KEYWORD_METRICS_UPDATE_LIMIT_REACHED')
      return Promise.reject(error)
    }
    if (isReportLimitBulkAnalysis) {
      const { default: useBulkAnalysisStore } = await import(
        'features/bulk-analysis/store/use-bulk-analysis-store'
      )
      useBulkAnalysisStore.getState().setBulkAnalysisFreeTrialModal(true)
      return Promise.reject(error)
    }
    if (isReportLimitReached && !isReportLimitBulkAnalysis) {
      sendMessage('LIMIT_REACHED')
      return Promise.reject(error)
    }
    if (isBearerTokenInvalid && retry < 4) {
      const forceRefresh = getTokenPromise === null
      return getToken(forceRefresh).then((token) => {
        error.config.headers.Authorization = `Bearer ${token}`
        return axiosInstance(error.config)
      })
    }
    if (isInvalidUserSession) {
      sendMessage('SIGN_OUT_WARNING')
      return Promise.reject(error)
    }

    const retryIf = error.config?.retryIf || (() => true)

    if (error?.config?.retry > 1 && retryIf(error)) {
      const errDelay = error.config.retryDelay || 2000
      return new Promise((resolve) => {
        setTimeout(
          () =>
            resolve(
              axiosInstance({
                ...error.config,
                retry: error.config.retry - 1,
              }),
            ),
          errDelay,
        )
      })
    }

    throw error
  },
)

const isJsonBlob = (data) =>
  data instanceof Blob && data.type === 'application/json'

export { axiosInstance, rawAxiosInstance, getTokenTTL }
